import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import { patientsService, PatientMealPlan } from '../../services/patients/patientsService';
import DietPlanLayout from './DietPlanLayout';
import PatientMealPlanTab from './PatientMealPlanTab';
import { SECTION_LABELS } from '../../components/layout/DashboardSidebar/sidebarItems';

/**
 * Edit an existing meal plan.
 * Route: /patients/:patientId/diet-plan/:goalId/daily-meals/:planId/edit
 */
const MealPlanEditPage: React.FC = () => {
  const { patientId, goalId, planId } = useParams<{ patientId: string; goalId: string; planId: string }>();
  const [mealPlan, setMealPlan] = useState<PatientMealPlan | null>(null);
  const [patientName, setPatientName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDietPlan = useCallback(async () => {
    if (!patientId || !goalId) {
      setError('Invalid patient or diet plan identifier');
      setLoading(false);
      return;
    }
    const numericPatientId = Number(patientId);
    const numericGoalId = Number(goalId);
    if (Number.isNaN(numericPatientId) || Number.isNaN(numericGoalId)) {
      setError('Invalid patient or diet plan identifier');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await patientsService.getPatientMealPlanByGoalId(numericPatientId, numericGoalId);
      if (!data) {
        setError('Diet plan not found');
        return;
      }
      setMealPlan(data);
      try {
        const patients = await patientsService.getPatients();
        const patient = patients.find((p) => p.id === numericPatientId);
        setPatientName(patient?.name ?? null);
      } catch {
        setPatientName(null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load diet plan');
    } finally {
      setLoading(false);
    }
  }, [patientId, goalId]);

  useEffect(() => {
    loadDietPlan();
  }, [loadDietPlan]);

  if (!authService.isAuthenticated()) {
    return null;
  }

  const mealsBasePath = patientId && goalId ? `/patients/${patientId}/diet-plan/${goalId}/daily-meals` : undefined;
  const breadcrumbSegments = [
    { label: SECTION_LABELS['daily-meals'] ?? 'Meal Plans', to: mealsBasePath },
    { label: 'Edit Plan' },
  ];

  return (
    <DietPlanLayout
      patientId={patientId!}
      goalId={goalId!}
      patientName={patientName}
      mealPlan={mealPlan}
      breadcrumbSegments={breadcrumbSegments}
      pageTitle="Edit Plan"
      loading={loading}
      error={error}
    >
      <div
        style={{
          marginTop: '24px',
          marginBottom: '24px',
          padding: '20px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
        }}
      >
        <PatientMealPlanTab
          patientId={Number(patientId)}
          goalId={goalId ? Number(goalId) : undefined}
          mealPlanViewMode="edit"
          mealPlanIdFromUrl={planId}
        />
      </div>
    </DietPlanLayout>
  );
};

export default MealPlanEditPage;
